<template>
  <div
    v-loading="guestIsInitialLoading && eventLoadingStatus !== 'init'"
    :element-loading-text="loadingText"
    @drop.prevent="onFileDrop"
  >
    <RecycleScroller
      v-if="!guestIsInitialLoading"
      v-show="guestListIsVisible"
      v-slot="{ item: guest, index: guestIndex }"
      :items="guests"
      :item-size="listItemHeight"
      key-field="id"
      page-mode
      emit-update
      class="guest-list w-full flex flex-col flex-1 scrolling-touch"
      @update="onGuestListScrollerUpdate"
    >
      <GuestListItem
        :item="guest"
        :item-index="guestIndex"
        :item-height="listItemHeight"
        :avatar-chip-size="48"
        @guest-update="$emit('guest-update', $event)"
        @guest-delete="$emit('guest-delete', $event)"
      />
    </RecycleScroller>
    <div
      v-show="!guestListIsVisible"
      class="flex-1 flex flex-col items-center justify-center"
    >
      <GuestDropZone
        v-if="guestsIsEmpty && !guestIsInitialLoading"
        full-drop-zone
      />
      <p
        v-else-if="anyFilterIsSelected && !filteredGuestsCountTotal"
        class="text-xl text-primary"
      >
        No search results.
      </p>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { RecycleScroller } from 'vue-virtual-scroller';
import { supportedImportFileTypes } from '@/config/appConfig';
import GuestDropZone from '@/components/guest/GuestDropZone';
import GuestListItem from './GuestListItem';

export default {
  props: {
    guests: {
      type: Array,
      default() {
        return [];
      },
    },
    listItemHeight: {
      type: Number,
      default: 60,
    },
  },
  components: {
    RecycleScroller,
    GuestDropZone,
    GuestListItem,
  },
  computed: {
    ...mapState('user/events', {
      eventLoadingStatus: 'loadingStatus',
    }),
    ...mapState('guest', {
      guestIsInitialLoading: 'isInitialLoading',
      isLoadingSnapshot: 'isLoadingSnapshot',
      isDeletingGuests: 'isDeletingGuests',
      deletingGuestsTimerIsRunning: 'deletingGuestsTimerIsRunning',
    }),
    ...mapState('guest/import', {
      isImportingGuests: 'isImportingGuests',
    }),
    ...mapGetters('guest', {
      guestsIsEmpty: 'guestsIsEmpty',
    }),
    ...mapGetters('guest/filter', {
      filteredGuests: 'filteredGuests',
      anyFilterIsSelected: 'anyFilterIsSelected',
    }),
    filteredGuestsCountTotal() {
      return this.filteredGuests.guestsCounts.mainGuest + this.filteredGuests.guestsCounts.extraGuest;
    },
    guestListIsVisible() {
      return !this.guestIsInitialLoading && this.filteredGuestsCountTotal
        && (!this.guestsIsEmpty || (this.guestsIsEmpty && this.deletingGuestsTimerIsRunning));
    },
    loadingText() {
      if (this.eventLoadingStatus === 'init') return 'Loading event..';
      if (this.isDeletingGuests) return 'Deleting guests..';
      if (this.isImportingGuests) return 'Importing guests..';
      if (this.isLoadingSnapshot) return 'Loading guests..';
      return 'Loading guests..';
    },
  },
  methods: {
    ...mapActions('guest', {
      updateGuestPhotoURL: 'updateGuestPhotoURL',
    }),
    ...mapActions('guest/import', {
      updateGuestImportDialogIsVisible: 'updateDialogIsVisible',
      updateGuestImportSelectedFiles: 'updateSelectedFiles',
    }),
    onGuestListScrollerUpdate(startIndex, endIndex) {
      // Update guest photoURLs if visible and not set previously
      for (let i = startIndex; i < endIndex; i += 1) {
        if (this.guests[i].photoStoragePath && !this.guests[i].photoURL
          && this.guests[i].snapshotChange !== 'modified') {
          this.updateGuestPhotoURL(i);
        }
      }
    },
    onFileDrop(e) {
      const acceptedFileTypes = supportedImportFileTypes.split(',');
      const fileExtension = `.${e.dataTransfer.files[0].name.split('.').pop()}`;

      if (!acceptedFileTypes.includes(fileExtension)) {
        this.$alert('Your file type is invalid, select an Excel or CSV file and try again', 'Wrong file type', {
          confirmButtonText: 'Ok',
          closeOnClickModal: true,
          closeOnPressEscape: true,
        }).catch((error) => error);
      } else {
        this.updateGuestImportSelectedFiles(e.dataTransfer.files);
        this.updateGuestImportDialogIsVisible(true);
      }
    },
  },
};
</script>

<style scoped>
.guest-list {
  &.ready >>> .vue-recycle-scroller__item-view {
    @apply absolute w-full left-0 top-0;
  }

  >>> .vue-recycle-scroller__item-wrapper {
    @apply relative flex-1;
  }

  >>> .resize-observer {
    @apply hidden;
  }
}
</style>
